import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { App, Button, Card, Col, Input, InputNumber, Row, Select, Space, Spin, Typography } from 'antd';
import { CopyOutlined, DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { ProgramRefPicker } from '@/pages/Studio/ProgramRefPicker';
import { useFlyerCategories, useFlyerTemplate, useCreateFlyerTemplate, useUpdateFlyerTemplate, useSetTemplateBackground } from '@/hooks/useFlyers';
import type { FlyerFieldConfig, FlyerTemplateInput } from '@/types/flyer';
import type { PctRect } from '@/lib/flyer-canvas';
import { clampRect, fieldToRect, moveRect, rectToField } from '@/lib/flyer-canvas';
import { EditorCanvas } from './EditorCanvas';
import { FieldInspector } from './FieldInspector';
import { BackgroundPanel } from './BackgroundPanel';

const newField = (): FlyerFieldConfig => ({
  id: crypto.randomUUID().toUpperCase(),
  type: 'Text', label: 'Text',
  x_percent: 0.25, y_percent: 0.45, width_percent: 0.5, height_percent: 0.08,
  style_config: { color: '#000000', fontName: 'System', fontSize: 100, alignment: 'center', fontWeight: 'regular', isItalic: false, letterSpacing: 0, shadowRadius: 0 },
});

export function TemplateEditorPage() {
  const { message } = App.useApp();
  const nav = useNavigate();
  const { id: routeId } = useParams();
  const [params] = useSearchParams();
  const isNew = routeId === 'new';
  const templateId = isNew ? null : routeId ?? null;

  const tplQ = useFlyerTemplate(templateId);
  const catsQ = useFlyerCategories();
  const create = useCreateFlyerTemplate();
  const update = useUpdateFlyerTemplate();
  const setBg = useSetTemplateBackground();

  // Draft state — hydrated from the query once, then owned locally.
  const [name, setName] = useState('');
  const [nameJson, setNameJson] = useState<Record<string, string>>({});
  const [categoryId, setCategoryId] = useState<string>('');
  const [size, setSize] = useState({ width: 1080, height: 1920 });
  const [fields, setFields] = useState<FlyerFieldConfig[]>([]);
  const [sourceTopicNo, setSourceTopicNo] = useState<string | null>(params.get('topic'));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(isNew);
  const [hydrated, setHydrated] = useState(isNew);
  const [saving, setSaving] = useState(false);
  const pendingBg = params.get('bg'); // generation id to apply right after first save

  useEffect(() => {
    const t = tplQ.data;
    if (!t || hydrated) return;
    setName(t.name_json.en ?? Object.values(t.name_json)[0] ?? '');
    setNameJson(t.name_json ?? {});
    setCategoryId(t.category_id);
    setSize({ width: t.width, height: t.height });
    setFields(t.fields_config.map((f) => rectToField(f, clampRect(fieldToRect(f)))));
    setSourceTopicNo(t.source_topic_no);
    setHydrated(true);
  }, [tplQ.data, hydrated]);

  const selected = useMemo(() => fields.find((f) => f.id === selectedId) ?? null, [fields, selectedId]);
  const mutate = (fn: (prev: FlyerFieldConfig[]) => FlyerFieldConfig[]) => { setFields(fn); setDirty(true); };

  // Keyboard: arrows nudge the selected field (Shift = ×10), Esc deselects.
  // Skipped while typing in inputs. Browser-level unload guard while dirty.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;
      if (e.key === 'Escape') { setSelectedId(null); return; }
      if (!selectedId || !e.key.startsWith('Arrow')) return;
      e.preventDefault();
      const step = e.shiftKey ? 0.05 : 0.005;
      const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
      const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
      mutate((prev) => prev.map((f) => (f.id === selectedId ? rectToField(f, moveRect(fieldToRect(f), dx, dy)) : f)));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const onChangeRect = (id: string, r: PctRect) =>
    mutate((prev) => prev.map((f) => (f.id === id ? rectToField(f, r) : f)));
  const onChangeField = (nf: FlyerFieldConfig) =>
    mutate((prev) => prev.map((f) => (f.id === nf.id ? nf : f)));
  const addField = () => { const f = newField(); mutate((p) => [...p, f]); setSelectedId(f.id); };
  const duplicateField = () => {
    if (!selected) return;
    const f = { ...selected, id: crypto.randomUUID().toUpperCase(), y_percent: Math.min(0.9, selected.y_percent + 0.05) };
    mutate((p) => [...p, f]); setSelectedId(f.id);
  };
  const removeField = () => { if (selected) { mutate((p) => p.filter((f) => f.id !== selected.id)); setSelectedId(null); } };

  const save = async () => {
    if (saving) return;
    if (!name.trim()) { message.warning('Enter a template name'); return; }
    if (!categoryId) { message.warning('Pick a category'); return; }
    setSaving(true);
    const body: FlyerTemplateInput = {
      category_id: categoryId, name_json: { ...nameJson, en: name.trim() },
      width: size.width, height: size.height,
      fields_config: fields.map(({ default_text: _dt, ...rest }) => rest),
      source_topic_no: sourceTopicNo,
    };
    try {
      if (templateId) {
        await update.mutateAsync({ id: templateId, b: body });
        message.success('Saved');
      } else {
        const row = await create.mutateAsync(body);
        if (pendingBg) {
          try { await setBg.mutateAsync({ id: row.id, generationId: pendingBg }); }
          catch (e) { message.warning(`Template created, background failed: ${(e as Error).message}`); }
        }
        message.success('Created');
        nav(`/flyers/templates/${row.id}`, { replace: true });
      }
      setDirty(false);
    } catch (e) { message.error((e as Error).message); }
    finally { setSaving(false); }
  };

  if (!isNew && tplQ.isLoading) return <Spin style={{ marginTop: 80 }} />;
  if (!isNew && tplQ.isError) return <Typography.Text type="danger">{(tplQ.error as Error).message}</Typography.Text>;

  return (
    <div style={{ maxWidth: 1280 }}>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }} wrap>
        <Space wrap>
          <Input placeholder="Template name" value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }} style={{ width: 220 }} />
          <Select placeholder="Category" value={categoryId || undefined} style={{ width: 180 }}
            options={(catsQ.data ?? []).map((c) => ({ value: c.id, label: c.name_json.en ?? c.id }))}
            onChange={(v) => { setCategoryId(v); setDirty(true); }} />
          <Space size={4}>
            <InputNumber min={100} value={size.width} onChange={(v) => { setSize((s) => ({ ...s, width: v ?? s.width })); setDirty(true); }} style={{ width: 100 }} />
            ×
            <InputNumber min={100} value={size.height} onChange={(v) => { setSize((s) => ({ ...s, height: v ?? s.height })); setDirty(true); }} style={{ width: 100 }} />
          </Space>
        </Space>
        <Space>
          <Button icon={<PlusOutlined />} onClick={addField}>Field</Button>
          <Button icon={<CopyOutlined />} onClick={duplicateField} disabled={!selected}>Duplicate</Button>
          <Button icon={<DeleteOutlined />} danger onClick={removeField} disabled={!selected}>Delete</Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={save} loading={saving}>
            {dirty ? 'Save*' : 'Save'}
          </Button>
        </Space>
      </Space>
      <Row gutter={12}>
        <Col xs={24} md={16}>
          <EditorCanvas
            width={size.width} height={size.height}
            backgroundUrl={tplQ.data?.background_image_url ?? null}
            fields={fields} selectedId={selectedId}
            onSelect={setSelectedId} onChangeRect={onChangeRect} />
        </Col>
        <Col xs={24} md={8}>
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <Card size="small" title="Program">
              <ProgramRefPicker
                initialTopicNo={sourceTopicNo ?? undefined}
                onPick={(p) => { setSourceTopicNo(p.topicNo); setDirty(true); }}
                onClear={() => { setSourceTopicNo(null); setDirty(true); }} />
              {sourceTopicNo && (
                <Space size={4}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>bound: {sourceTopicNo}</Typography.Text>
                  <Button type="link" size="small" style={{ padding: 0, height: 'auto', fontSize: 12 }}
                    onClick={() => { setSourceTopicNo(null); setDirty(true); }}>Unbind</Button>
                </Space>
              )}
            </Card>
            <Card size="small" title="Background">
              <BackgroundPanel templateId={templateId} backgroundUrl={tplQ.data?.background_image_url ?? null}
                sourceTopicNo={sourceTopicNo}
                onChanged={() => tplQ.refetch()} />
            </Card>
            <Card size="small" title="Field">
              <FieldInspector field={selected} programBound={!!sourceTopicNo} onChange={onChangeField} />
            </Card>
          </Space>
        </Col>
      </Row>
    </div>
  );
}
